import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Modal from '@cloudscape-design/components/modal'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { addManualProviderSchema, type AddManualProviderFormData } from '@/lib/schemas'
import type { Provider } from '@/lib/types'

interface AddManualProviderModalProps {
  clientId: string
  visible: boolean
  onDismiss: () => void
  onAdd: (provider: Provider) => void
}

export default function AddManualProviderModal({
  clientId,
  visible,
  onDismiss,
  onAdd,
}: AddManualProviderModalProps) {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<AddManualProviderFormData>({
    resolver: zodResolver(addManualProviderSchema),
    defaultValues: { name: '', email: '', npi: '' },
  })

  function onSubmit(data: AddManualProviderFormData) {
    const uid = `manual-${Date.now()}`
    const provider: Provider = {
      _id: uid,
      client_id: clientId,
      provider_name: data.name,
      specialty: '',
      practice_name: '',
      city: '',
      state: '',
      npi: data.npi?.trim() || uid,
      email: data.email,
      status: 'new',
      prescribing_volume_annual_usd: null,
      dme_product_mix: [],
      cms_rank_by_volume: null,
      talking_points: [],
      engagement_signals: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    onAdd(provider)
    reset()
  }

  function handleDismiss() {
    reset()
    onDismiss()
  }

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Add provider manually"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>Cancel</Button>
            <Button variant="primary" onClick={() => { void handleSubmit(onSubmit)() }}>
              Add provider
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          <Box variant="p" color="text-body-secondary">
            Add a provider who is not in your database. The email will be used to send the campaign.
          </Box>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <FormField label="Provider name" errorText={errors.name?.message}>
                <Input
                  placeholder="e.g. Dr. Jane Smith"
                  value={field.value}
                  onChange={({ detail }) => field.onChange(detail.value)}
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <FormField label="Email address" errorText={errors.email?.message}>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={field.value}
                  onChange={({ detail }) => field.onChange(detail.value)}
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="npi"
            render={({ field }) => (
              <FormField
                label={
                  <Box variant="span">
                    NPI{' '}
                    <Box variant="span" color="text-body-secondary">(optional)</Box>
                  </Box>
                }
                errorText={errors.npi?.message}
              >
                <Input
                  placeholder="10-digit NPI number"
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                />
              </FormField>
            )}
          />
        </SpaceBetween>
      </Form>
    </Modal>
  )
}
